import {
  QUESTION_PROMPT_MAX_LENGTH,
  ANSWER_MAX_LENGTH,
  MAX_DECIMALS_LIMIT,
  MIN_DECIMALS_LIMIT,
} from 'containers/QuestionnairePage/constants';
import {
  isMatrixConstantSumType,
  isMatrixOpenEndedQuestionType,
} from 'utils/nodeTypes';
import { get, isEmpty, reject, isNil, isNumber } from 'lodash';
import { getPromptFromHtml } from 'components/FormattedInput/helpers';
import { hasLogicVisibilityError } from 'components/VisibilityActionButton/utils';
import { getPreviousNodeRefIdsForNode } from '../SingleMultipleQuestion/utils';
import {
  getMinScalepointsCount,
  getMinStatementsCount,
  isCarriedForward,
  isMatrixAnswerEmpty,
} from './utils';
import {
  MATRIX_OPEN_ENDED_MAX_STATEMENTS,
  MATRIX_OPEN_ENDED_MAX_SCALEPOINTS,
  MATRIX_CONSTANT_SUM_TYPES,
  MATRIX_OPEN_ENDED_ALLOWED_EMPTY_ST_COUNT,
} from './constants';
import { FORWARD_ANSWER_AS } from '../../NodeSettings/settings/utils/constants';
import {
  hasIncompleteCarryForward,
  getAllCarriedForwardCount,
  onlyAnswerSelectionWithPossibleZeroCarryForward,
} from '../../NodeSettings/settings/utils/CarryForward';

function getMatrixOpenEndedLimits(questionType) {
  const minStatementsLimit = getMinStatementsCount(questionType);
  const minScalepointsLimit = getMinScalepointsCount(questionType);
  return {
    minStatementsLimit,
    maxStatementsLimit: MATRIX_OPEN_ENDED_MAX_STATEMENTS,
    minScalepointsLimit,
    maxScalepointsLimit: MATRIX_OPEN_ENDED_MAX_SCALEPOINTS,
  };
}

function validate({
  node,
  nodes,
  previousNodesPerNode = {},
  answersWithLogicFromOutcomes,
  screeningQuestions = [],
  allNodes = [],
}) {
  const { refId, prompt, matrix, type, carryForward = [] } = node;
  const isConstantSum = isMatrixConstantSumType(type);
  const isOpenEnded = isMatrixOpenEndedQuestionType(type);
  const errors = { answerTooLong: {} };
  const matrixAnswers = get(matrix, 'matrixAnswers', []);
  const matrixColumns = get(matrix, 'columns', []);
  const openEndedConfig = get(matrix, 'openEndedConfig', {});
  const { gridType } = openEndedConfig;
  const openEndedValidation = get(matrix, 'validation.openEndedValidation', {});
  const { min, max, decimals, total } = openEndedValidation;
  const columnLabels = matrixColumns.map(({ beforeLabel }) => beforeLabel);

  const carryForwardEnabled = Boolean(carryForward.length);

  const {
    minStatementsLimit,
    maxStatementsLimit,
    minScalepointsLimit,
    maxScalepointsLimit,
  } = getMatrixOpenEndedLimits(type);

  if (isEmpty(prompt)) {
    errors.missingPrompt = true;
  }

  const questionTooLong = prompt && prompt.length > QUESTION_PROMPT_MAX_LENGTH;
  if (questionTooLong) {
    errors.questionTooLong = true;
  }

  const hasInvalidAnswers =
    matrixAnswers.length > MATRIX_OPEN_ENDED_ALLOWED_EMPTY_ST_COUNT &&
    matrixAnswers.some(isMatrixAnswerEmpty);

  if (hasInvalidAnswers) {
    errors.emptyRows = true;
  }

  if (
    matrixAnswers.length <= MATRIX_OPEN_ENDED_ALLOWED_EMPTY_ST_COUNT &&
    matrixAnswers.some((answer) => {
      const strippedAnswer = getPromptFromHtml(
        answer.beforeLabel,
        undefined,
        false,
      );
      const hasWhitespaces =
        strippedAnswer.length > 0 && strippedAnswer.trim() === '';
      return hasWhitespaces;
    })
  ) {
    errors.whitespacesForStatements = true;
  }

  // answer prompt max length check
  matrixAnswers.forEach((matrixAnswer) => {
    if (matrixAnswer.beforeLabel.length > ANSWER_MAX_LENGTH) {
      errors.answerTooLong[matrixAnswer.answerRefId] = true;
    }
  });

  const nonEmptyAnswers = reject(
    matrixAnswers,
    (matrixAnswer) =>
      isMatrixAnswerEmpty(matrixAnswer) || isCarriedForward(matrixAnswer),
  );

  const nonEmptyMatrixColumns = reject(
    matrixColumns,
    (matrixColumn) =>
      isMatrixAnswerEmpty(matrixColumn) || isCarriedForward(matrixColumn),
  );

  if (
    !carryForwardEnabled &&
    matrixAnswers.length > MATRIX_OPEN_ENDED_ALLOWED_EMPTY_ST_COUNT &&
    (nonEmptyAnswers.length < minStatementsLimit ||
      nonEmptyAnswers.length > maxStatementsLimit)
  ) {
    errors.outOfLimitsRows = true;
  }

  if (
    !isOpenEnded &&
    !carryForwardEnabled &&
    (nonEmptyMatrixColumns.length < minScalepointsLimit ||
      nonEmptyMatrixColumns.length > maxScalepointsLimit)
  ) {
    errors.outOfLimitsColumns = true;
  }

  if (isEmpty(errors.answerTooLong)) {
    delete errors.answerTooLong;
  }

  if (
    !isOpenEnded &&
    columnLabels.some((label) => isEmpty(getPromptFromHtml(label)))
  ) {
    errors.emptyScalepointLabels = true;
  }

  const hasMin = isNumber(min);
  const hasMax = isNumber(max);
  const hasDecimals = !isNil(decimals);

  if ((hasMin && !hasMax) || (hasMax && !hasMin)) {
    errors.minMaxRangeInvalid = true;
  } else if (hasMin && hasMax && min >= max) {
    errors.minGreaterThanMax = true;
  }

  if (
    (hasDecimals && decimals < MIN_DECIMALS_LIMIT) ||
    decimals > MAX_DECIMALS_LIMIT
  ) {
    errors.decimalLimitOutOfBounds = true;
  }
  let carriedCounts;
  if (carryForwardEnabled) {
    carriedCounts = getAllCarriedForwardCount(carryForward, allNodes);
    const carriedAnswersCount = carriedCounts[FORWARD_ANSWER_AS.ANSWERS];
    const carriedScorepointsCount =
      carriedCounts[FORWARD_ANSWER_AS.SCOREPOINTS];

    if (carriedAnswersCount) {
      const hasEnoughCFanswers =
        carriedAnswersCount.min + nonEmptyAnswers.length >= minStatementsLimit;
      if (!hasEnoughCFanswers) {
        errors.missingAnswersWithCF = true;
      }
      if (
        nonEmptyAnswers.length + carriedAnswersCount.max >
        maxStatementsLimit
      ) {
        errors.tooManyAnswersWithCF = true;
      }
    }

    if (carriedScorepointsCount) {
      const hasEnoughCFscorepoints =
        carriedScorepointsCount.min + nonEmptyMatrixColumns.length >=
        minScalepointsLimit;
      if (!hasEnoughCFscorepoints) {
        errors.missingScorepointsWithCF = true;
      }
      if (
        nonEmptyMatrixColumns.length + carriedScorepointsCount.max >
        maxScalepointsLimit
      ) {
        errors.tooManyScorepointsWithCF = true;
      }
    }

    const hasIncompleteCarryForwardConfigurations = hasIncompleteCarryForward(
      carryForward,
      allNodes,
    );
    if (hasIncompleteCarryForwardConfigurations) {
      errors.incompleteCarryForwardConfiguration = true;
    }

    const previousNodes = getPreviousNodeRefIdsForNode({
      refId,
      previousNodesPerNode,
      nodes,
      screeningNodes: screeningQuestions,
    });
    const hasUnusedCF = carryForward.some(
      ({ questionRefId }) => !previousNodes.includes(questionRefId),
    );
    if (hasUnusedCF) {
      errors.hasUnusedCF = true;
    }

    if (
      (matrixAnswers.every(isCarriedForward) ||
        matrixColumns.every(isCarriedForward)) &&
      onlyAnswerSelectionWithPossibleZeroCarryForward(carryForward)
    ) {
      if (errors.missingAnswersWithCF) {
        errors.onlyAnswerSelectionWithPossibleZeroCarryForwardRows = true;
        delete errors.missingAnswersWithCF;
      }
      if (errors.missingScorepointsWithCF) {
        errors.onlyAnswerSelectionWithPossibleZeroCarryForwardColumns = true;
        delete errors.missingScorepointsWithCF;
      }
    }
  }

  if (isConstantSum) {
    if (hasMin) {
      if (min < 0) {
        errors.minRangeTooSmall = true;
      }
    }
    if (gridType === MATRIX_CONSTANT_SUM_TYPES.MUST_TOTAL) {
      if (!isNumber(total)) {
        errors.constantSumTotalError = true;
      }
      if (total > 0) {
        let carriedColumnsCounts = { min: 0, max: 0 };
        if (
          carryForwardEnabled &&
          carriedCounts?.[FORWARD_ANSWER_AS.SCOREPOINTS]
        ) {
          carriedColumnsCounts = carriedCounts[FORWARD_ANSWER_AS.SCOREPOINTS];
        }

        const nonCFColumnCount = matrixColumns.filter(
          (column) => !column.isCarriedForward,
        ).length;
        const availableColumnRange = {
          min: nonCFColumnCount + carriedColumnsCounts.min,
          max: nonCFColumnCount + carriedColumnsCounts.max,
        };

        if (hasMin) {
          if (min >= total) {
            errors.minMaxGreaterThanSumTotal = true;
          }
          if (
            min * availableColumnRange.min > total ||
            min * availableColumnRange.max > total
          ) {
            errors.minMaxRangeInvalidSumTotal = true;
          }
        }

        if (hasMax) {
          if (max > total) {
            errors.minMaxGreaterThanSumTotal = true;
          }
          if (
            max * availableColumnRange.min < total ||
            max * availableColumnRange.max < total
          ) {
            errors.minMaxRangeInvalidSumTotal = true;
          }
        }
        const totalDecimals = (total.toString().split('.')[1] || '').length;
        if (
          (hasDecimals && totalDecimals > decimals) ||
          (!hasDecimals && totalDecimals > 0)
        ) {
          errors.totalTooManyTotalDecimals = true;
        }
      }
    }
  }

  if (
    hasLogicVisibilityError({
      nodeType: node.type,
      isScreening: node.isScreening,
      answers: nonEmptyAnswers,
      carriedAnswers: matrixAnswers.filter(isCarriedForward),
      answersWithLogicFromOutcomes,
    })
  ) {
    errors.hasLogicVisibilityErrorOnRows = true;
  }

  if (
    hasLogicVisibilityError({
      nodeType: node.type,
      isScreening: node.isScreening,
      answers: nonEmptyMatrixColumns,
      carriedAnswers: matrixColumns.filter(isCarriedForward),
      answersWithLogicFromOutcomes,
    })
  ) {
    errors.hasLogicVisibilityErrorOnColumns = true;
  }

  return errors;
}

export default validate;
